/**
 * Design System — Theme assembly.
 *
 * A `Theme` is the full design-token set for one visual identity. The default
 * theme's colour layer derives from the canonical `@tradelogic/ui-tokens`
 * package (G06 CR-08 — sole visual SoT); light + dark share every scale except
 * the semantic colour layer. Per-tenant white-label overrides are applied on
 * top at runtime by the ThemeProvider (see ../ThemeProvider). The `slds-*`
 * entries in `themeRecipes` are optional admin-selectable brand presets
 * (overrides layered on the base), not the base source of truth.
 */
import { lightColors, darkColors, type SemanticColors } from './tokens/colors.js';
import { typography, type Typography } from './tokens/typography.js';
import { spacing, type Spacing } from './tokens/spacing.js';
import { radius, type Radius } from './tokens/radius.js';
import { borderWidth, type BorderWidth } from './tokens/borderWidth.js';
import { shadow, type Shadow } from './tokens/shadows.js';
import { animation, type Animation } from './tokens/animation.js';
import { breakpoint, type Breakpoint } from './tokens/breakpoints.js';
import { zIndex, type ZIndex } from './tokens/zindex.js';

export type ThemeMode = 'light' | 'dark';

export interface Theme {
  readonly id: string;
  readonly name: string;
  readonly colors: { readonly light: SemanticColors; readonly dark: SemanticColors };
  readonly typography: Typography;
  readonly spacing: Spacing;
  readonly radius: Radius;
  readonly borderWidth: BorderWidth;
  readonly shadow: Shadow;
  readonly animation: Animation;
  readonly breakpoint: Breakpoint;
  readonly zIndex: ZIndex;
}

/** Canonical default theme — colour layer derived from @tradelogic/ui-tokens. */
export const sldsTheme: Theme = {
  id: 'slds',
  name: 'Salesforce Lightning',
  colors: { light: lightColors, dark: darkColors },
  typography,
  spacing,
  radius,
  borderWidth,
  shadow,
  animation,
  breakpoint,
  zIndex,
};

export const defaultTheme: Theme = sldsTheme;

/**
 * Per-tenant white-label override. Admin-configurable subset (values that a
 * tenant may re-brand) applied on top of the base theme at runtime. Any field
 * omitted falls back to the base theme. Keyed by the same semantic color names
 * so a tenant can re-brand without touching component code.
 */
export interface TenantThemeOverride {
  readonly themeId?: string;
  readonly mode?: ThemeMode;
  readonly colors?: {
    readonly light?: Partial<SemanticColors>;
    readonly dark?: Partial<SemanticColors>;
  };
  readonly fontFamilySans?: string;
  readonly logoUrl?: string;
}

/**
 * Recipe presets — named starting points an admin can pick then fine-tune.
 * Each is a `TenantThemeOverride` layered on the SLDS base. The blends below
 * nudge the primary/secondary toward the named systems (Fluent = deeper blue;
 * Oracle Redwood = warmer). Admins can override any single token afterward.
 */
export const themeRecipes: Readonly<Record<string, { name: string; override: TenantThemeOverride }>> = {
  'slds-100': {
    name: 'Salesforce Lightning (100%)',
    override: {},
  },
  'slds-fluent': {
    name: 'SLDS 70% + Microsoft Fluent 20% + Custom 10%',
    override: {
      colors: {
        light: { brandPrimary: '#0F6CBD', brandPrimaryHover: '#0C5A9E', textLink: '#0F6CBD', borderFocus: '#0F6CBD', success: '#2E844A' },
      },
      fontFamilySans: '"Segoe UI", "Salesforce Sans", Inter, Roboto, Arial, sans-serif',
    },
  },
  'slds-fluent-oracle': {
    name: 'SLDS 70% + Fluent 20% + Oracle Fusion 10%',
    override: {
      colors: {
        light: { brandPrimary: '#1B6DC1', brandPrimaryHover: '#155596', textLink: '#1B6DC1', borderFocus: '#1B6DC1' },
      },
    },
  },
};

export { lightColors, darkColors, typography, spacing, radius, borderWidth, shadow, animation, breakpoint, zIndex };
export type { SemanticColors };
